import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { UserSummary } from "../lib/contracts";
import { endpoints } from "../lib/contracts";
import { ApiError, request, resetCsrf } from "../lib/api";

type AuthStatus = "checking" | "authenticated" | "anonymous";
interface AuthContextValue {
  status: AuthStatus;
  user: UserSummary | null;
  expired: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  dismissExpired: () => void;
}
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [user, setUser] = useState<UserSummary | null>(null);
  const [expired, setExpired] = useState(false);

  const restore = useCallback(async () => {
    try {
      const current = await request<UserSummary>(endpoints.auth.me);
      setUser(current);
      setStatus("authenticated");
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) console.warn("Session restore failed safely");
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  useEffect(() => { void restore(); }, [restore]);
  useEffect(() => {
    const handler = () => { if (status === "authenticated") setExpired(true); };
    window.addEventListener("workbench:session-expired", handler);
    return () => window.removeEventListener("workbench:session-expired", handler);
  }, [status]);

  const login = useCallback(async (email: string, password: string) => {
    resetCsrf();
    const current = await request<UserSummary>(endpoints.auth.login, { method: "POST", body: { email, password } });
    setUser(current);
    setStatus("authenticated");
    setExpired(false);
  }, []);
  const logout = useCallback(async () => {
    try { await request<void>(endpoints.auth.logout, { method: "POST" }); } finally {
      resetCsrf(); setUser(null); setStatus("anonymous"); setExpired(false);
    }
  }, []);
  const dismissExpired = useCallback(() => { setExpired(false); setUser(null); setStatus("anonymous"); }, []);
  const value = useMemo(() => ({ status, user, expired, login, logout, dismissExpired }), [status, user, expired, login, logout, dismissExpired]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
